import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import saleService from '../services/saleService';
import platformService from '../services/platformService';
import { getCountryName } from '../utils/countries';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ProductStatsFilteredScreen = () => {
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [groupBy, setGroupBy] = useState('day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [locationFilters, setLocationFilters] = useState({});
  const [availableCities, setAvailableCities] = useState([]);
  const [filters, setFilters] = useState({
    country: '',
    city: '',
    platform: '',
    hasConversion: ''
  });
  
  // Filtros temporales (los que se muestran en la interfaz antes de aplicarlos)
  const [tempFilters, setTempFilters] = useState({
    timeRange: 'month',
    startDate: '',
    endDate: '',
    country: '',
    city: '',
    platform: '',
    hasConversion: ''
  });
  
  const [platforms, setPlatforms] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const originalStatsRef = useRef(null);
  const [dataLoadTrigger, setDataLoadTrigger] = useState(0);
  const [isStacked, setIsStacked] = useState(false);
  const [chartSalesFilter, setChartSalesFilter] = useState('all'); // 'all' o 'approved_pending'

  // Función para leer parámetros de URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlCountry = urlParams.get('country');
    const urlCity = urlParams.get('city');
    const urlPlatform = urlParams.get('platform');
    const urlTimeRange = urlParams.get('timeRange');
    const urlHasConversion = urlParams.get('hasConversion');
    const urlStartDate = urlParams.get('startDate');
    const urlEndDate = urlParams.get('endDate');

    console.log('🔗 [URL] Parámetros leídos desde URL:', {
      urlCountry,
      urlCity,
      urlPlatform,
      urlTimeRange,
      urlHasConversion,
      urlStartDate,
      urlEndDate
    });

    // Aplicar filtros desde URL
    if (urlCountry || urlCity || urlPlatform || urlTimeRange || urlHasConversion || urlStartDate || urlEndDate) {
      const newFilters = {
        country: urlCountry || '',
        city: urlCity || '',
        platform: urlPlatform || '',
        hasConversion: urlHasConversion || ''
      };
      
      // Aplicar a los filtros aplicados (que se usan para cargar datos)
      setFilters(prev => ({
        ...prev,
        ...newFilters
      }));

      // IMPORTANTE: También aplicar a los filtros temporales (que se muestran en la interfaz)
      setTempFilters(prev => ({
        ...prev,
        ...newFilters,
        timeRange: urlTimeRange || prev.timeRange,
        startDate: urlStartDate || prev.startDate,
        endDate: urlEndDate || prev.endDate
      }));

      // Aplicar timeRange y fechas
      if (urlTimeRange) {
        setTimeRange(urlTimeRange);
        
        // Si hay fechas específicas, usarlas
        if (urlStartDate && urlEndDate) {
          setStartDate(urlStartDate);
          setEndDate(urlEndDate);
        } else {
          // Si no hay fechas específicas pero sí timeRange, calcular fechas
          const dateRange = calculateDateRange(urlTimeRange);
          if (dateRange) {
            setStartDate(dateRange.startDate);
            setEndDate(dateRange.endDate);
          }
        }
      }

      console.log('🔗 [URL] Filtros aplicados desde URL:', {
        newFilters,
        timeRange: urlTimeRange,
        startDate: urlStartDate,
        endDate: urlEndDate
      });
    }
  }, [location.search]);

  // Función para procesar los valores de ingresos
  const processRevenueValue = (value) => {
    if (typeof value === 'string') {
      return parseFloat(value) || 0;
    }
    return parseFloat(value) || 0;
  };

  // Función para formatear fechas según el agrupamiento
  const formatDate = (date, groupBy) => {
    const d = new Date(date);
    switch (groupBy) {
      case 'day':
        return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
      case 'week':
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return `${weekStart.getDate()}/${weekStart.getMonth() + 1} - ${weekEnd.getDate()}/${weekEnd.getMonth() + 1}`;
      case 'month':
        return d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
      case 'year':
        return d.getFullYear().toString();
      default:
        return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    }
  };

  // Función para obtener la clave de agrupación según el período
  const getGroupKey = (date, groupBy) => {
    const d = new Date(date);
    switch (groupBy) {
      case 'day':
        return d.toISOString().split('T')[0];
      case 'week':
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay());
        return weekStart.toISOString().split('T')[0];
      case 'month':
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      case 'year':
        return d.getFullYear().toString();
      default:
        return d.toISOString().split('T')[0];
    }
  };

  // Función para calcular el rango de fechas según el filtro seleccionado
  const calculateDateRange = (timeRange, customStartDate, customEndDate) => {
    // Crear fechas en la zona horaria de Madrid
    const now = new Date();
    let endDate = new Date(now);
    let startDate = new Date(now);

    if (timeRange === 'custom' && customStartDate && customEndDate) {
      return {
        startDate: customStartDate,
        endDate: customEndDate
      };
    }

    switch (timeRange) {
      case 'lastWeek':
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 7);
        endDate = new Date(endDate);
        break;
      case 'lastMonth':
        startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - 1);
        startDate.setDate(1);
        endDate = new Date(endDate);
        endDate.setDate(0); // Último día del mes anterior
        break;
      case 'lastYear':
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 1);
        startDate.setMonth(0);
        startDate.setDate(1);
        endDate = new Date(endDate);
        endDate.setFullYear(endDate.getFullYear() - 1);
        endDate.setMonth(11);
        endDate.setDate(31);
        break;
      case 'last7days':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'last30days':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case 'last90days':
        startDate.setDate(endDate.getDate() - 90);
        break;
      case 'thisWeek':
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - endDate.getDay() + 1); // Lunes de esta semana
        endDate = new Date(endDate);
        endDate.setDate(endDate.getDate() - endDate.getDay() + 7); // Domingo de esta semana
        break;
      case 'thisMonth':
        startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
        break;
      case 'thisYear':
        startDate = new Date(endDate.getFullYear(), 0, 1);
        endDate = new Date(endDate.getFullYear(), 11, 31);
        break;
      case 'week':
      case 'month':
      case 'year':
      default:
        // Para compatibilidad con los valores antiguos, mantener el comportamiento original
        return null;
    }

    // Ajustar las horas para el inicio y fin del día
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    // Formatear las fechas en YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };

  // Función para obtener todas las fechas en un rango
  const getAllDatesInRange = (startDate, endDate, groupBy) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const lastDate = new Date(endDate);

    while (currentDate <= lastDate) {
      dates.push(getGroupKey(currentDate.toISOString(), groupBy));
      switch (groupBy) {
        case 'day':
          currentDate.setDate(currentDate.getDate() + 1);
          break;
        case 'week':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case 'month':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        case 'year':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;
      }
    }
    return dates;
  };

  // Función para agrupar ventas por período
  const groupSalesByPeriod = (sales, groupBy, selectedProductIds, allProducts, salesFilter = 'all') => {
    if (!sales || sales.length === 0) return { periodData: [], topProducts: [] };

    // Filtrar las ventas según el tipo seleccionado
    let filteredSales = sales;
    if (salesFilter === 'approved_pending') {
      filteredSales = sales.filter(sale => sale.status === 'approved' || sale.status === 'pending');
    }

    // Debug: Verificar las propiedades de la primera venta
    if (filteredSales.length > 0) {
      console.log('Sample sale object:', filteredSales[0]);
      console.log('Available properties:', Object.keys(filteredSales[0]));
      console.log('Commission values:', {
        commissionEUR: filteredSales[0].commissionEUR,
        commission: filteredSales[0].commission,
        currency: filteredSales[0].currency,
        exchangeRateUsed: filteredSales[0].exchangeRateUsed
      });
    }

    // Usar los productos seleccionados en lugar de calcular automáticamente
    const selectedProducts = allProducts?.filter(product => 
      selectedProductIds.includes(product.productId)
    ) || [];

    const dates = filteredSales.map(sale => new Date(sale.saleDate));
    const startDate = new Date(Math.min(...dates));
    const endDate = new Date(Math.max(...dates));
    const allDates = getAllDatesInRange(startDate, endDate, groupBy);
    
    const grouped = {};
    allDates.forEach(date => {
      grouped[date] = {
        date: date,
        sales: 0,
        revenue: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        products: {}
      };
      
      // Inicializar datos para cada producto seleccionado
      selectedProducts.forEach(product => {
        grouped[date].products[product.productId] = {
          sales: 0,
          revenue: 0
        };
      });
    });
    
    filteredSales.forEach(sale => {
      const groupKey = getGroupKey(sale.saleDate, groupBy);
      if (grouped[groupKey]) {
        grouped[groupKey].sales += 1;
        
        // Usar directamente commissionEUR que ya viene calculado del backend
        const commissionValue = parseFloat(sale.commissionEUR) || 0;
        grouped[groupKey].revenue += commissionValue;
        
        // Agrupar por producto si está seleccionado
        const productId = sale.Product.id;
        if (grouped[groupKey].products[productId]) {
          grouped[groupKey].products[productId].sales += 1;
          grouped[groupKey].products[productId].revenue += commissionValue;
        }
        
        if (sale.status === 'pending') {
          grouped[groupKey].pending += 1;
        } else if (sale.status === 'approved') {
          grouped[groupKey].approved += 1;
        } else {
          grouped[groupKey].rejected += 1;
        }
      }
    });

    const periodData = Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
    console.log('Grouped sales by period:', periodData);
    console.log('Total revenue from grouping:', periodData.reduce((sum, item) => sum + item.revenue, 0));
    console.log('Selected products:', selectedProducts);
    
    return { periodData, topProducts: selectedProducts };
  };

  // Manejar cambio en selección de rango de tiempo
  const handleTimeRangeChange = (e) => {
    const newTimeRange = e.target.value;
    setTempFilters(prev => ({
      ...prev,
      timeRange: newTimeRange
    }));
    
    // Si no es rango personalizado, actualizar fechas automáticamente
    if (newTimeRange !== 'custom') {
      const dateRange = calculateDateRange(newTimeRange);
      if (dateRange) {
        setTempFilters(prev => ({
          ...prev,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }));
      }
    }
  };

  // Aplicar filtros
  const applyFilters = () => {
    setTimeRange(tempFilters.timeRange);
    setStartDate(tempFilters.startDate);
    setEndDate(tempFilters.endDate);
    setFilters({
      country: tempFilters.country,
      city: tempFilters.city,
      platform: tempFilters.platform,
      hasConversion: tempFilters.hasConversion
    });
  };
  
  // Limpiar filtros
  const clearFilters = () => {
    const defaultFilters = {
      timeRange: 'month',
      startDate: '',
      endDate: '',
      country: '',
      city: '',
      platform: '',
      hasConversion: ''
    };
    
    setTempFilters(defaultFilters);
    setTimeRange('month');
    setStartDate('');
    setEndDate('');
    setFilters({
      country: '',
      city: '',
      platform: '',
      hasConversion: ''
    });
  };

  // Cargar filtros de ubicación
  useEffect(() => {
    const fetchLocationFilters = async () => {
      try {
        const filtersData = await saleService.getLocationFilters();
        setLocationFilters(filtersData);
        
        // Si hay un país seleccionado desde URL, actualizar ciudades disponibles
        if (tempFilters.country && tempFilters.country !== 'none' && filtersData[tempFilters.country]) {
          setAvailableCities(filtersData[tempFilters.country]);
        }
      } catch (error) {
        console.error('❌ [ERROR] Error al cargar filtros de ubicación:', error);
      }
    };
    fetchLocationFilters();
  }, [tempFilters.country]);

  // Cargar plataformas
  useEffect(() => {
    const loadPlatforms = async () => {
      try {
        const data = await platformService.getActivePlatforms();
        setPlatforms(data || []);
      } catch (err) {
        console.error('❌ [ERROR] Error al cargar plataformas:', err);
      }
    };
    loadPlatforms();
  }, []);

  // Manejar cambio de país
  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    
    if (selectedCountry && selectedCountry !== 'none' && locationFilters[selectedCountry]) {
      setAvailableCities(locationFilters[selectedCountry]);
    } else {
      setAvailableCities([]);
    }
    
    setTempFilters(prev => ({
      ...prev,
      country: selectedCountry,
      city: '' // Resetear ciudad cuando cambia el país
    }));
  };

  // Función para convertir timeRange de URL a formato backend
  const convertTimeRangeToBackend = (timeRange) => {
    const mapping = {
      'lastWeek': 'week',
      'lastMonth': 'month', 
      'lastYear': 'year',
      'last7days': 'week',
      'last30days': 'month',
      'last90days': 'month', // Se manejará con fechas personalizadas
      'thisWeek': 'week',
      'thisMonth': 'month',
      'thisYear': 'year'
    };
    return mapping[timeRange] || timeRange || 'month';
  };

  // Cargar estadísticas
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const requestParams = {};
        
        console.log('📅 [FRONTEND] Parámetros de entrada:', {
          timeRange,
          startDate,
          endDate,
          filters
        });
        
        // Manejar fechas personalizadas o rangos predefinidos
        if (timeRange === 'custom' && startDate && endDate) {
          requestParams.startDate = startDate;
          requestParams.endDate = endDate;
          console.log('📅 [FRONTEND] Usando fechas personalizadas:', {
            startDate,
            endDate
          });
        } else if (['lastWeek', 'lastMonth', 'lastYear', 'last7days', 'last30days', 'last90days', 'thisWeek', 'thisMonth', 'thisYear'].includes(timeRange)) {
          const dateRange = calculateDateRange(timeRange, startDate, endDate);
          console.log('📅 [FRONTEND] Calculando rango para:', timeRange, 'resultado:', dateRange);
          if (dateRange) {
            requestParams.startDate = dateRange.startDate;
            requestParams.endDate = dateRange.endDate;
            console.log('📅 [FRONTEND] Usando fechas calculadas:', {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate
            });
          }
        } else {
          // Para compatibilidad con valores antiguos (week, month, year)
          const backendTimeRange = convertTimeRangeToBackend(timeRange);
          requestParams.timeRange = backendTimeRange;
          console.log('📅 [FRONTEND] Usando timeRange convertido:', backendTimeRange);
        }
        
        // Solo agregar filtros que tienen valores válidos
        if (filters.country && filters.country !== 'none') {
          requestParams.country = filters.country;
        }
        
        if (filters.city && filters.city !== '') {
          requestParams.city = filters.city;
        }
        
        if (filters.platform && filters.platform !== '') {
          requestParams.platform = filters.platform;
        }
        
        if (filters.hasConversion && filters.hasConversion !== '') {
          requestParams.hasConversion = filters.hasConversion;
        }

        console.log('📊 [FRONTEND] Parámetros finales enviados al backend:', requestParams);
        
        const data = await saleService.getProductStats(requestParams);
        
        if (data) {
          // Usar los datos del backend sin procesarlos automáticamente
          originalStatsRef.current = data;
          setStats(data);
          setDataLoadTrigger(prev => prev + 1);
        } else {
          originalStatsRef.current = null;
          setStats(null);
          setDataLoadTrigger(prev => prev + 1);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('❌ [ERROR] Error fetching stats:', err);
        console.error('❌ [ERROR] Error details:', {
          message: err.message,
          status: err.status,
          response: err.response?.data
        });
        setError('Error al cargar las estadísticas');
        setLoading(false);
      }
    };

    fetchStats();
  }, [filters, timeRange, groupBy, startDate, endDate]);

  // Inicializar productos seleccionados con los 3 primeros
  useEffect(() => {
    if (stats?.products && stats.products.length > 0) {
      const top3Products = stats.products.slice(0, 3).map(product => product.productId);
      setSelectedProducts(top3Products);
    }
  }, [stats?.products]);

  // Procesar datos de gráficos cuando cambien los productos seleccionados
  useEffect(() => {
    const originalStats = originalStatsRef.current;
    if (!originalStats) return;
    
    if (originalStats.recentConversions && originalStats.products && selectedProducts.length > 0) {
      const { periodData, topProducts } = groupSalesByPeriod(
        originalStats.recentConversions, 
        groupBy, 
        selectedProducts, 
        originalStats.products,
        chartSalesFilter
      );
      
      setStats(prevStats => ({
        ...originalStats,
        salesByDay: periodData,
        revenueByDay: periodData.map(item => ({
          date: item.date,
          revenue: item.revenue
        })),
        topProducts: topProducts
      }));
    } else {
      // Si no hay conversiones o productos seleccionados, crear arrays vacíos
      setStats(prevStats => ({
        ...originalStats,
        salesByDay: [],
        revenueByDay: [],
        topProducts: []
      }));
    }
  }, [selectedProducts, groupBy, dataLoadTrigger, isStacked, chartSalesFilter]);

  // Función para manejar cambios en checkboxes
  const handleProductSelection = (productId, isChecked) => {
    if (isChecked) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  const formatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR'
  });

  // Función para generar colores únicos para productos
  const generateProductColor = (index) => {
    const colors = [
      'rgb(255, 99, 132)',   // Rojo
      'rgb(54, 162, 235)',   // Azul
      'rgb(255, 205, 86)',   // Amarillo
      'rgb(75, 192, 192)',   // Verde agua
      'rgb(153, 102, 255)',  // Púrpura
      'rgb(255, 159, 64)',   // Naranja
      'rgb(199, 199, 199)',  // Gris
      'rgb(83, 102, 255)',   // Azul índigo
      'rgb(255, 99, 255)',   // Magenta
      'rgb(99, 255, 132)',   // Verde lima
      'rgb(255, 159, 255)',  // Rosa
      'rgb(159, 255, 99)',   // Verde claro
    ];
    
    // Si tenemos más productos que colores predefinidos, generar colores HSL
    if (index >= colors.length) {
      const hue = (index * 137.508) % 360; // Usar proporción áurea para distribución uniforme
      return `hsl(${hue}, 70%, 50%)`;
    }
    
    return colors[index];
  };

  // Función para obtener la versión rgba de un color
  const getBackgroundColor = (color, opacity = 0.5) => {
    if (color.startsWith('hsl')) {
      return color.replace('hsl', 'hsla').replace(')', `, ${opacity})`);
    }
    return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
  };

  // Configuración del gráfico de ventas
  const salesChartData = {
    labels: stats?.salesByDay?.map(item => formatDate(item.date, groupBy)) || [],
    datasets: [
      {
        label: 'Total de Ventas',
        data: stats?.salesByDay?.map(item => item.sales) || [],
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.3)',
        tension: 0.1,
        borderWidth: 3,
        fill: false,
        stack: isStacked ? 'total' : undefined
      },
      ...(stats?.topProducts?.map((product, index) => {
        const color = generateProductColor(index);
        return {
          label: product.productName && product.productName.length > 30 ? product.productName.substring(0, 30) + '...' : product.productName || `Producto ${index + 1}`,
          data: stats?.salesByDay?.map(item => item.products[product.productId]?.sales || 0) || [],
          borderColor: color,
          backgroundColor: isStacked ? getBackgroundColor(color, 0.8) : getBackgroundColor(color, 0.2),
          tension: 0.1,
          borderWidth: 2,
          borderDash: isStacked ? [] : [5, 5],
          fill: isStacked ? 'origin' : false,
          stack: isStacked ? 'products' : undefined
        };
      }) || [])
    ]
  };

  const salesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          maxWidth: 200,
          usePointStyle: true
        }
      },
      title: {
        display: true,
        text: `Ventas por ${groupBy === 'day' ? 'Día' : 
          groupBy === 'week' ? 'Semana' : 
          groupBy === 'month' ? 'Mes' : 'Año'}`
      }
    },
    scales: {
      x: {
        stacked: isStacked
      },
      y: {
        beginAtZero: true,
        stacked: isStacked,
        title: {
          display: true,
          text: 'Número de Ventas'
        }
      }
    }
  };

  // Configuración del gráfico de ingresos
  const revenueChartData = {
    labels: stats?.revenueByDay?.map(item => formatDate(item.date, groupBy)) || [],
    datasets: [
      {
        label: 'Total de Comisiones',
        data: stats?.revenueByDay?.map(item => processRevenueValue(item.revenue)) || [],
        backgroundColor: 'rgba(54, 162, 235, 0.3)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 3,
        tension: 0.1,
        fill: false,
        stack: isStacked ? 'total' : undefined
      },
      ...(stats?.topProducts?.map((product, index) => {
        const color = generateProductColor(index);
        return {
          label: product.productName && product.productName.length > 30 ? product.productName.substring(0, 30) + '...' : product.productName || `Producto ${index + 1}`,
          data: stats?.salesByDay?.map(item => processRevenueValue(item.products[product.productId]?.revenue || 0)) || [],
          backgroundColor: isStacked ? getBackgroundColor(color, 0.8) : getBackgroundColor(color, 0.5),
          borderColor: color,
          borderWidth: 2,
          tension: 0.1,
          borderDash: isStacked ? [] : [5, 5],
          fill: isStacked ? 'origin' : false,
          stack: isStacked ? 'products' : undefined
        };
      }) || [])
    ]
  };

  const revenueChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          maxWidth: 200,
          usePointStyle: true
        }
      },
      title: {
        display: true,
        text: `Comisiones por ${groupBy === 'day' ? 'Día' : 
          groupBy === 'week' ? 'Semana' : 
          groupBy === 'month' ? 'Mes' : 'Año'}`
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${formatter.format(context.raw)}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: isStacked
      },
      y: {
        beginAtZero: true,
        stacked: isStacked,
        title: {
          display: true,
          text: 'Ingresos (€)'
        },
        ticks: {
          callback: function(value) {
            return formatter.format(value);
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div>
          <h1 className="page-title">Estadísticas por Ubicación</h1>
          <div className="page-subtitle mt-2">
            {filters.country && (
              <span className="font-semibold text-base-content">{getCountryName(filters.country)}</span>
            )}
            {filters.city && (
              <>
                <span className="mx-2">•</span>
                <span>{filters.city}</span>
              </>
            )}
          </div>
        </div>
        <div className="page-actions">
          <select 
            className="select select-bordered"
            value={tempFilters.timeRange}
            onChange={handleTimeRangeChange}
          >
            <option value="lastWeek">Semana Anterior</option>
            <option value="lastMonth">Mes Anterior</option>
            <option value="lastYear">Año Anterior</option>
            <option value="last7days">Últimos 7 días</option>
            <option value="last30days">Últimos 30 días</option>
            <option value="last90days">Últimos 90 días</option>
            <option value="thisWeek">Semana Actual</option>
            <option value="thisMonth">Mes Actual</option>
            <option value="thisYear">Año Actual</option>
            <option value="week">Última semana</option>
            <option value="month">Último mes</option>
            <option value="year">Último año</option>
            <option value="custom">Rango Personalizado</option>
          </select>
          
          {tempFilters.timeRange === 'custom' && (
            <>
              <input
                type="date"
                className="input input-bordered"
                value={tempFilters.startDate}
                onChange={(e) => setTempFilters(prev => ({ ...prev, startDate: e.target.value }))}
                placeholder="Fecha inicio"
              />
              <input
                type="date"
                className="input input-bordered"
                value={tempFilters.endDate}
                onChange={(e) => setTempFilters(prev => ({ ...prev, endDate: e.target.value }))}
                placeholder="Fecha fin"
              />
            </>
          )}
          
          <Link to="/product-stats" className="btn btn-outline">
            Volver a Estadísticas
          </Link>
        </div>
      </div>

      {/* Filtros */}
      <div className="card bg-base-100 shadow-xl mb-6">
        <div className="card-body">
          <h2 className="card-title">Filtros</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">País</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.country}
                onChange={handleCountryChange}
              >
                <option value="">Todos los países</option>
                <option value="none">No definido</option>
                {Object.keys(locationFilters).map((country) => (
                  <option key={country} value={country}>
                    {getCountryName(country)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Ciudad</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.city}
                onChange={(e) => {
                  setTempFilters(prev => ({ ...prev, city: e.target.value }));
                }}
                disabled={!tempFilters.country || tempFilters.country === 'none'}
              >
                <option value="">Todas las ciudades</option>
                {availableCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Plataforma</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.platform}
                onChange={(e) => {
                  setTempFilters(prev => ({ ...prev, platform: e.target.value }));
                }}
              >
                <option value="">Todas las plataformas</option>
                {platforms.map((platform) => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Tipo de Conversión</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.hasConversion}
                onChange={(e) => {
                  setTempFilters(prev => ({ ...prev, hasConversion: e.target.value }));
                }}
              >
                <option value="">Todas las ventas</option>
                <option value="true">Ventas con conversión</option>
                <option value="false">Ventas sin conversión</option>
              </select>
            </div>
          </div>
          
          <div className="flex justify-end mt-4 gap-2">
            {loading && (
              <div className="flex items-center">
                <span className="loading loading-spinner loading-sm mr-2"></span>
                <span>Cargando...</span>
              </div>
            )}
            <button 
              className="btn btn-ghost"
              onClick={clearFilters}
            >
              Limpiar Filtros
            </button>
            <button 
              className="btn btn-primary"
              onClick={applyFilters}
              disabled={loading}
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Resumen de métricas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="stat bg-base-100 shadow rounded-lg">
          <div className="stat-title">Ventas Totales</div>
          <div className="stat-value">{stats?.totalSales || 0}</div>
          <div className="stat-desc">En el período seleccionado</div>
        </div>
        <div className="stat bg-base-100 shadow rounded-lg">
          <div className="stat-title">Comisiones Totales</div>
          <div className="stat-value">{formatter.format(stats?.totalRevenue || 0)}</div>
          <div className="stat-desc">En el período seleccionado</div>
        </div>
        <div className="stat bg-base-100 shadow rounded-lg">
          <div className="stat-title">Estado de Ventas</div>
          <div className="stat-value text-sm">
            <div className="flex flex-col gap-1">
              <span className="text-success">✅ {stats?.approvedSales || 0} Aprobadas</span>
              <span className="text-warning">⏳ {stats?.pendingSales || 0} Pendientes</span>
              <span className="text-error">❌ {stats?.rejectedSales || 0} Rechazadas</span>
            </div>
          </div>
        </div>
        <div className="stat bg-base-100 shadow rounded-lg">
          <div className="stat-title">Valor Medio</div>
          <div className="stat-value">{formatter.format(stats?.averageValue || 0)}</div>
          <div className="stat-desc">Por venta</div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 gap-6">
        {/* Selectores de configuración */}
        <div className="flex justify-end gap-4 mb-2">
          <select 
            className="select select-bordered select-sm"
            value={groupBy}
            onChange={(e) => {
              setGroupBy(e.target.value);
            }}
          >
            <option value="day">Por Día</option>
            <option value="week">Por Semana</option>
            <option value="month">Por Mes</option>
            <option value="year">Por Año</option>
          </select>
          
          <select 
            className="select select-bordered select-sm"
            value={chartSalesFilter}
            onChange={(e) => {
              setChartSalesFilter(e.target.value);
            }}
          >
            <option value="all">Todas las ventas</option>
            <option value="approved_pending">Ventas aprobadas o pendientes</option>
          </select>
          
          <select 
            className="select select-bordered select-sm"
            value={isStacked ? 'stacked' : 'normal'}
            onChange={(e) => {
              setIsStacked(e.target.value === 'stacked');
            }}
          >
            <option value="normal">Gráficos Superpuestos</option>
            <option value="stacked">Gráficos Apilados</option>
          </select>
        </div>

        {/* Gráfico de ventas */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Ventas por {groupBy === 'day' ? 'Día' : 
              groupBy === 'week' ? 'Semana' : 
              groupBy === 'month' ? 'Mes' : 'Año'}</h2>
            <div className="h-96">
              <Line data={salesChartData} options={salesChartOptions} />
            </div>
          </div>
        </div>

        {/* Gráfico de ingresos */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Comisiones por {groupBy === 'day' ? 'Día' : 
              groupBy === 'week' ? 'Semana' : 
              groupBy === 'month' ? 'Mes' : 'Año'}</h2>
            <div className="h-96">
              <Line data={revenueChartData} options={revenueChartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de productos */}
      <div className="card bg-base-100 shadow-xl mt-6">
        <div className="card-body">
          <h2 className="card-title">Productos</h2>
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>
                    <label className="label cursor-pointer">
                      <span className="label-text font-medium">Mostrar en gráficos</span>
                    </label>
                  </th>
                  <th>Producto</th>
                  <th>Plataforma</th>
                  <th>Ventas</th>
                  <th>Comisión Total</th>
                  <th>Comisión Media</th>
                  <th>% Canceladas</th>
                </tr>
              </thead>
              <tbody>
                {stats?.products?.map((product, index) => (
                  <tr key={index}>
                    <td>
                      <label className="label cursor-pointer justify-center">
                        <input 
                          type="checkbox" 
                          className="checkbox checkbox-primary" 
                          checked={selectedProducts.includes(product.productId)}
                          onChange={(e) => handleProductSelection(product.productId, e.target.checked)}
                        />
                      </label>
                    </td>
                    <td>
                      <Link 
                        to={`/product-stats/${product.productId}`}
                        className="link link-primary"
                      >
                        {product.productName}
                      </Link>
                    </td>
                    <td>
                      <span 
                        className="badge badge-outline"
                        style={{ 
                          '--badge-color': product.platformColor || '#808080'
                        }}
                      >
                        {product.platformName}
                      </span>
                    </td>
                    <td>{product.totalSales}</td>
                    <td>{formatter.format(product.totalCommissionEUR)}</td>
                    <td>{formatter.format(product.avgCommissionEUR)}</td>
                    <td>{product.canceledPercentage ? `${product.canceledPercentage}%` : '0%'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductStatsFilteredScreen; 
